import { Injectable } from '@angular/core';

@Injectable({
    'providedIn': 'root'
})
export class FirestorePathsService {
    private accountNumber = '';

    // Instead of storing collection references, we'll store collection paths
    // These collections need to be defined early, that is before a user logs in
    private readonly backupFilesNamesPath = 'backupFilesNames';
    private readonly countriesPath = 'countries';
    private readonly documentsPath = 'docs';
    private readonly errorsPath = 'errors';
    private readonly firebaseFunctionsPath = 'firebaseFunctions';
    private readonly isoCountriesPath = 'isoCountries';
    private readonly isoTimezonesPath = 'isoTimezones';
    private readonly pricesPath = 'prices';
    private readonly skuActionsPath = 'skuActions';
    private readonly tasksPath = 'tasksQueue';
    private readonly userAccountMapsPath = 'userAccountMaps';
    private readonly zipcodesPath = 'zipcodes';
    private readonly smsFromNumbersPath = 'smsFromNumbers';

    // Account-specific collection paths
    private readonly accountsPath = 'accounts';
    private accountNumberPath!: string;
    private campaignsPath!: string;
    private campaignIncentivesPath!: string;
    private campaignIncentiveSetsPath!: string;
    private customersPath!: string;
    private dealershipPath!: string;
    private messagesPath!: string;
    private pickupRequestsPath!: string;
    private pickupResponsesPath!: string;
    private profilesPath!: string;
    private routesPath!: string;
    private serviceAreasPath!: string;
    private settingsPath!: string;
    private shuttlesPath!: string;
    private smsSentMessagesPath!: string;
    private summaryPath!: string;
    private ticketsPath!: string;
    private tripsPath!: string;
    private usersPath!: string;
    private vectorsPath!: string;

    // private firestore: Firestore
    constructor() {
        // Just use the paths defined above
    }

    // These paths need to be defined after a user logs in - they are account number dependent
    public setAccount(accountNumber: string): void {
        this.accountNumber = accountNumber;

        // Build account-specific paths
        this.accountNumberPath = `${this.accountsPath}/${accountNumber}`;
        this.campaignsPath = `${this.accountNumberPath}/campaigns`;
        this.campaignIncentivesPath = `${this.accountNumberPath}/incentives`;
        this.campaignIncentiveSetsPath = `${this.accountNumberPath}/incentiveSets`;
        this.customersPath = `${this.accountNumberPath}/customers`;
        this.dealershipPath = `${this.accountNumberPath}/profiles/dealershipProfile`;
        this.messagesPath = `${this.accountNumberPath}/messages`;

        //accounts/00000-0000-00000/profiles/dealershipProfile

        this.routesPath = `${this.accountNumberPath}/routes`;
        this.pickupRequestsPath = `${this.accountNumberPath}/pickupRequests`;
        this.pickupResponsesPath = `${this.accountNumberPath}/pickupResponses`;
        this.profilesPath = `${this.accountNumberPath}/profiles`;
        this.serviceAreasPath = `${this.accountNumberPath}/serviceAreas`;
        this.settingsPath = `${this.accountNumberPath}/settings/setting`;
        this.shuttlesPath = `${this.accountNumberPath}/shuttles`;
        this.smsSentMessagesPath = `${this.accountNumberPath}/smsSentMessages`;
        this.summaryPath = `${this.accountNumberPath}/summaries`;
        this.ticketsPath = `${this.accountNumberPath}/tickets`;
        this.tripsPath = `${this.accountNumberPath}/trips`;
        this.usersPath = `${this.accountNumberPath}/users`;
        this.vectorsPath = `${this.accountNumberPath}/vectors`;
    }

    public setAccountForAnonymousUsers(accountNumber: string): void {
        this.accountNumber = accountNumber;
        this.accountNumberPath = `${this.accountsPath}/${accountNumber}`;
        this.vectorsPath = `${this.accountNumberPath}/vectors`;
    }

    public getAccountChildPath(accountNumber: string, childNodeName: string): string {
        return `${this.accountsPath}/${accountNumber}/${childNodeName}`;
    }

    public setAccountNumber(accountNumber: string): void {
        this.setAccount(accountNumber);
    }

    public getAccountNumberPath(): string {
        return this.accountNumberPath;
    }

    public getAccountProfilePath(): string {
        return `${this.accountsPath}/${this.accountNumber}/profiles/accountProfile`;
    }

    public getAccountProfilePathByAccountNumber(accountNumber: string): string {
        return `${this.accountsPath}/${accountNumber}/profiles/accountProfile`;
    }

    public getAccountsPath(): string {
        return this.accountsPath;
    }

    public getBackupFileNamesPath(): string {
        return this.backupFilesNamesPath;
    }

    public getCampaignsPath(): string {
        return this.campaignsPath;
    }

    public getCampaignIncentivesPath(): string {
        return this.campaignIncentivesPath;
    }

    public getCampaignIncentiveSetsPath(): string {
        return this.campaignIncentiveSetsPath;
    }

    public getCampaignIncentivesColByAccountNumber(accountNumber: string): string {
        return `${this.accountsPath}/${accountNumber}/incentives`;
    }

    public getCountriesPath(): string {
        return this.countriesPath;
    }

    public getCustomersPath(): string {
        return this.customersPath;
    }

    public getCustomerColByAccountNumber(accountNumber: string): string {
        return `${this.accountsPath}/${accountNumber}/customers`;
    }

    public getDealershipPathByAccountNumber(accountNumber: string): string {
        return `${this.accountsPath}/${accountNumber}/profiles/dealershipProfile`;
    }

    public getDealershipPath(): string {
        return this.dealershipPath;
    }

    public getDocumentsPath(): string {
        return this.documentsPath;
    }

    public getFirebaseFunctionsPath(): string {
        return this.firebaseFunctionsPath;
    }

    public getErrorsPath(): string {
        return this.errorsPath;
    }

    public getIsoCountriesPath(): string {
        return this.isoCountriesPath;
    }

    public getMessagesPath(): string {
        return this.messagesPath;
    }

    public getIsoTimezonesPath(): string {
        return `${this.isoTimezonesPath}/allTimezones`;
    }

    public getNetPromoterScoresPath(): string {
        // Returning null string to match previous behavior
        return '';
    }

    public getOrdersPath(): string {
        // Returning empty string to match previous behavior
        return '';
    }

    public getPickupRequestsPath(): string {
        return this.pickupRequestsPath;
    }

    public getPickupResponsesPath(): string {
        return this.pickupResponsesPath;
    }

    public getPricesPath(): string {
        return this.pricesPath;
    }

    public getProfilesPath(): string {
        return this.profilesPath;
    }

    public getRoutesPath(): string {
        return this.routesPath;
    }

    public getServiceAreasPath(): string {
        return this.serviceAreasPath;
    }

    public getSettingsPath(): string {
        return this.settingsPath;
    }

    public getSettingsByAccountNumberRef(accountNumber: string): string {
        return `${this.accountsPath}/${accountNumber}/settings/setting`;
    }

    public getShuttlesPath(): string {
        return this.shuttlesPath;
    }

    public getSkuActionsPath(): string {
        return this.skuActionsPath;
    }

    public getSmsFromNumbersPath(): string {
        return this.smsFromNumbersPath;
    }

    public getSmsSentMessagesPath(): string {
        return this.smsSentMessagesPath;
    }

    public getSmsSentMessagesAllRawPath(): string {
        return this.buildPath(['accounts', '?', 'smsSentMessages', 'all', 'raw']);
    }

    public getSmsSentMessagesCategoriesPath(category: string): string {
        return this.buildPath(['accounts', '?', 'smsSentMessages', 'categories', category]);
    }

    public getSummaryByAccountNumberPath(accountNumber: string): string {
        return `${this.accountsPath}/${accountNumber}/summaries/overallSummary`;
    }

    public getSummaryPath(): string {
        return this.summaryPath;
    }

    public getTasksPath(): string {
        return this.tasksPath;
    }

    public getTicketsPath(): string {
        return this.ticketsPath;
    }

    public getTripPath(tripUid: string): string {
        return this.buildPath(['accounts', '?', 'trips', tripUid]);
    }

    public getTripsPath(): string {
        return this.tripsPath;
    }

    public getTripTicketsPath(tripUid: string): string {
        return this.buildPath(['accounts', '?', 'trips', tripUid, 'tickets']);
    }

    public getTripActualLocationPath(tripUid: string): string {
        return this.buildPath(['accounts', '?', 'trips', tripUid, 'actualLocations']);
    }

    public getUsersPath(): string {
        return this.usersPath;
    }

    public getUserAccountMapsPath(): string {
        return this.userAccountMapsPath;
    }

    public getVectorsPath(): string {
        return this.vectorsPath;
    }

    public getZipcodesPath(): string {
        return this.zipcodesPath;
    }

    public getZipcodes2Path(): string {
        return this.zipcodesPath;
    }

    public getProfileByAccountNumberRef(accountNumber: string): string {
        return `${this.accountsPath}/${accountNumber}/accountProfiles/accountProfile`;
    }

    public getUsersColByAccountNumber(accountNumber: string): string {
        return `${this.accountsPath}/${accountNumber}/users`;
    }

    // Helper method to build paths
    public buildPath(nodeNames: string[]): string {
        const idx = nodeNames.indexOf('?');
        if (idx > -1) {
            nodeNames[idx] = this.accountNumber;
        }

        return nodeNames.join('/');
    }

    // // Keep this method for backward compatibility but return string path
    // public buildCollection(nodeNames: string[]): string {
    //     return this.buildPath(nodeNames);
    // }
}